/**
 * Booking persistence helper for localStorage
 * Handles saving/loading booking slice state with SSR safety
 */

const STORAGE_KEY = 'hirego_booking_state';

/**
 * Safely get booking state from localStorage
 * @returns {Object|null} Parsed booking state or null if not found/invalid
 */
export const loadBookingState = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return null;
        }

        const parsed = JSON.parse(stored);
        
        // Basic validation - ensure it's an object
        if (!parsed || typeof parsed !== 'object') {
            console.warn('[BookingPersistence] Invalid stored data format, clearing...');
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }

        return parsed;
    } catch (error) {
        console.error('[BookingPersistence] Error loading booking state:', error);
        // Clear corrupted data
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (clearError) {
            console.error('[BookingPersistence] Error clearing corrupted data:', clearError);
        }
        return null;
    }
};

/**
 * Save booking state to localStorage
 * @param {Object} bookingState - The booking slice state to persist
 */
export const saveBookingState = (bookingState) => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!bookingState || typeof bookingState !== 'object') {
        console.warn('[BookingPersistence] Invalid booking state, skipping save');
        return;
    }

    try {
        // Create a safe copy - exclude any non-serializable values if needed
        const stateToSave = { ...bookingState };
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
        // Handle quota exceeded or other storage errors gracefully
        if (error.name === 'QuotaExceededError') {
            console.warn('[BookingPersistence] localStorage quota exceeded, clearing old data...');
            try {
                localStorage.removeItem(STORAGE_KEY);
                // Try once more
                localStorage.setItem(STORAGE_KEY, JSON.stringify(bookingState));
            } catch (retryError) {
                console.error('[BookingPersistence] Failed to save after clearing:', retryError);
            }
        } else {
            console.error('[BookingPersistence] Error saving booking state:', error);
        }
    }
};

/**
 * Clear persisted booking state from localStorage and related sessionStorage entries
 */
export const clearBookingState = () => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        // Clear localStorage booking state
        localStorage.removeItem(STORAGE_KEY);
        
        // Also clear sessionStorage entries created by useBookingSubmit and useBookingForm
        sessionStorage.removeItem('booking_form1');
        sessionStorage.removeItem('booking_raw_form1_backup');
        sessionStorage.removeItem('booking_complete_raw_data');
        sessionStorage.removeItem('booking_last_submission_attempt');
        sessionStorage.removeItem('last_successful_submission');
        sessionStorage.removeItem('last_failed_submission');
        
        // Clear emergency localStorage fallback if it exists
        localStorage.removeItem('booking_raw_form1_emergency');
    } catch (error) {
        console.error('[BookingPersistence] Error clearing booking state:', error);
    }
};

